import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur } from '../../lib/utils';
import { type UnifiedRow, type QuoteItem } from '../../lib/scadenziario';
import { ProjectStatus } from '@masamune/shared';

/**
 * MoneyPipeline — il fiume del denaro in 5 stadi:
 *
 *   Inviati → Firmati → Progetti attivi → Da incassare → Incassato
 *
 * Ogni cella è una LENTE sui dati esistenti, mai una lista nuova: il click
 * setta i filtri della pagina (stessi useUrlState keys: tab/type/status) e
 * porta alla tab Documenti; "Progetti attivi" naviga alla divisione.
 * `data-active` accende la cella quando i filtri correnti corrispondono.
 *
 * Classi dal contratto CSS di fase (src/styles/index.css, "Lama e Fiume"):
 * .money-pipeline .mp-cell .mp-cell--solid .mp-label .mp-amount .mp-count .mp-chevron
 */

// Riga progetto: campi minimi che servono alla pipeline (stessa forma
// dell'endpoint usato da DivisionPage: GET /projects?pageSize=200).
interface PipelineProject {
  id: string;
  status: ProjectStatus;
  revenue: number;
}

// "Attivi" = il lavoro che diventerà fattura: tutto tranne PREVENTIVO
// (non ancora firmato) e CHIUSO (già a valle del fiume).
const ACTIVE_PROJECT_STATUSES: ProjectStatus[] = [
  ProjectStatus.ATTIVO, ProjectStatus.IN_CORSO, ProjectStatus.REVISIONE, ProjectStatus.IN_PAUSA,
];

// Default dei param URL (stessi della pagina): a valore = default, param rimosso.
const URL_DEFAULTS: Record<string, string> = { tab: 'scadenziario', type: 'all', status: 'all' };

export function MoneyPipeline({ rows }: { rows: UnifiedRow[] }) {
  const navigate = useNavigate();
  // Filtri correnti della pagina (stessi keys URL → data-active coerente)
  const [docType] = useUrlState<string>('type', 'all');
  const [statusFilter] = useUrlState<string>('status', 'all');
  const [, setSearchParams] = useSearchParams();

  // Progetti: stessa forma endpoint di DivisionPage, ma cross-divisione e
  // con queryKey dedicata (non collide con le cache per-divisione).
  const { data: projData } = useQuery({
    queryKey: ['projects', 'pipeline'],
    queryFn: () => api.get<{ items: PipelineProject[] }>('/projects?pageSize=200'),
  });

  // Reduce client-side dei 5 stadi (somma totali + count, scadute a parte)
  const stages = useMemo(() => {
    const sum = (xs: UnifiedRow[]) => xs.reduce((a, r) => a + Number(r.total), 0);
    const inviati = rows.filter((r) => r.kind === 'quote' && ['INVIATO', 'VISUALIZZATO'].includes(r.status));
    // ACCETTATO = firmato non ancora convertito (i convertiti hanno status CONVERTITO;
    // guard difensiva su convertedToInvoice per non contare due volte il denaro)
    const firmati = rows.filter((r) =>
      r.kind === 'quote' && r.status === 'ACCETTATO' && !(r.raw as QuoteItem).convertedToInvoice);
    const daIncassare = rows.filter((r) =>
      r.kind === 'invoice' && r.invoiceType === 'FATTURA' && ['EMESSA', 'INVIATA', 'SCADUTA'].includes(r.status));
    const scaduteCount = daIncassare.filter((r) => r.status === 'SCADUTA').length;
    const incassato = rows.filter((r) =>
      r.kind === 'invoice' && r.invoiceType === 'FATTURA' && r.status === 'PAGATA');
    return {
      inviati:     { total: sum(inviati),     count: inviati.length },
      firmati:     { total: sum(firmati),     count: firmati.length },
      daIncassare: { total: sum(daIncassare), count: daIncassare.length, scaduteCount },
      incassato:   { total: sum(incassato),   count: incassato.length },
    };
  }, [rows]);

  const progetti = useMemo(() => {
    const attivi = (projData?.items ?? []).filter((p) => ACTIVE_PROJECT_STATUSES.includes(p.status));
    return { total: attivi.reduce((a, p) => a + (Number(p.revenue) || 0), 0), count: attivi.length };
  }, [projData]);

  // Lente: un click = set atomico di tab+type+status (un solo history entry,
  // stesso pattern setParamsBatch della pagina) → tabella Documenti filtrata.
  const goToLens = (type: string, status: string) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      for (const [k, v] of Object.entries({ tab: 'documenti', type, status })) {
        if (v === URL_DEFAULTS[k]) sp.delete(k);
        else sp.set(k, v);
      }
      return sp;
    });
  };
  const lensActive = (type: string, status: string) => docType === type && statusFilter === status;

  return (
    <div className="money-pipeline mb-4" role="group" aria-label="Flusso del denaro in 5 stadi">
      <PipelineCell
        label="Inviati"
        amount={stages.inviati.total}
        countLabel={`${stages.inviati.count} preventivi`}
        active={lensActive('quote', 'INVIATO')}
        title="Preventivi in attesa di firma — apri la lista"
        onClick={() => goToLens('quote', 'INVIATO')}
      />
      <PipelineCell
        label="Firmati"
        amount={stages.firmati.total}
        countLabel={`${stages.firmati.count} da fatturare`}
        active={lensActive('quote', 'ACCETTATO')}
        title="Preventivi firmati non ancora convertiti — apri la lista"
        onClick={() => goToLens('quote', 'ACCETTATO')}
      />
      <PipelineCell
        label="Progetti attivi"
        amount={progetti.total}
        countLabel={`${progetti.count} progetti`}
        active={false}
        title="Lavoro in corso — vai alla divisione"
        onClick={() => navigate('/divisions/product')}
      />
      <PipelineCell
        label="Da incassare"
        amount={stages.daIncassare.total}
        countLabel={`${stages.daIncassare.count} fatture`}
        extra={stages.daIncassare.scaduteCount > 0
          ? <Chip tone="alert" dot>{stages.daIncassare.scaduteCount} scadute</Chip>
          : null}
        active={lensActive('FATTURA', 'EMESSA')}
        title="Fatture aperte (emesse, inviate, scadute) — apri la lista"
        onClick={() => goToLens('FATTURA', 'EMESSA')}
      />
      <PipelineCell
        label="Incassato"
        amount={stages.incassato.total}
        countLabel={`${stages.incassato.count} pagate`}
        active={lensActive('FATTURA', 'PAGATA')}
        title="Fatture pagate — apri la lista"
        onClick={() => goToLens('FATTURA', 'PAGATA')}
        solid
        last
      />
    </div>
  );
}

// Cella-stadio: label + chevron verso lo stadio successivo, importo animato,
// chip count (+ eventuale sotto-chip semantica, es. scadute in rosso).
function PipelineCell({ label, amount, countLabel, extra, active, solid, last, title, onClick }: {
  label: string;
  amount: number;
  countLabel: string;
  extra?: React.ReactNode;
  active: boolean;
  solid?: boolean;
  last?: boolean;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={solid ? 'mp-cell mp-cell--solid' : 'mp-cell'}
      data-active={active ? 'true' : undefined}
      aria-pressed={active}
      title={title}
      onClick={onClick}
    >
      <span className="flex items-center justify-between w-full gap-2">
        <span className="mp-label">{label}</span>
        {!last && <Icon name="chevronR" size={12} className="mp-chevron" />}
      </span>
      <span className="mp-amount">€ <AnimatedNumber value={amount} format={fmtEur} /></span>
      <span className="flex items-center gap-1.5 flex-wrap">
        <span className="mp-count">{countLabel}</span>
        {extra}
      </span>
    </button>
  );
}
